import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import clsx from 'clsx'
import CopyToClipboard from 'react-copy-to-clipboard'
// @TODO: Delete after scrollToAnchor is moved into ScrollingContext
import { scroller } from 'react-scroll'

import { ActionType } from '$extensionSrc/__generated__/graphql'
import { RecommendationAutomationDef } from '$extensionSrc/automations/automationMachineInterfaces'
import { startAutomationMachineForRecommendation } from '$extensionSrc/automations/automationStageMachine'
import { State } from '$extensionSrc/automations/facebook/fbUnfriend'
import { InstagramAccountVisibility } from '$extensionSrc/automations/instagram/accountEnums'
import ImpressionLogger from '$extensionSrc/components/ImpressionLogger'
import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import LogContextProvider from '$extensionSrc/components/LogContextProvider'
import Toast from '$extensionSrc/components/Toast'
import { SidebarContext } from '$extensionSrc/contexts/SidebarContextProvider'
import { updatePlatformUserAccountData } from '$extensionSrc/data/platform/platformUserAccountData'
import { AccessLevel } from '$extensionSrc/data/user/accessByPlatform'
import { addUserActionToLog } from '$extensionSrc/data/userActionLog'
import LinkIcon from '$extensionSrc/icons/chain-link-icon.png'
import Confetti from '$extensionSrc/icons/confetti.svg'
import SectionCollapseIcon from '$extensionSrc/icons/section-collapse-icon.svg'
import SectionExpandIcon from '$extensionSrc/icons/section-expand-icon.svg'
import RecommendationDisplayWrapper from '$extensionSrc/recommendations/recommendationDisplayWrapper'
import { RecommendationCardDisplayData } from '$extensionSrc/recommendations/recommendationProgressTypes'
import { isValidSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { RecommendationKeyEnum } from '$extensionSrc/recommendations/recommendationTypes'
import * as state from '$extensionSrc/state'
import * as communication from '$extensionSrc/utils/communication'
import { getTimeSinceInSeconds } from '$extensionSrc/utils/dateUtils'
import {
  AutomationType,
  FBRecommendationKey,
  PLATFORM,
  RecommendationCardType,
  RecommendationKey,
  RecommendationState,
  TwitterRecommendationKey,
} from '$extensionSrc/utils/enums'

import AutomatedBody from './AutomatedBody'
import Banner from './Banner'
import FbPhotoUntaggingBody from './FbPhotoUntaggingBody'
import FbUnfriendBody from './FbUnfriendBody'
import Header from './Header'
import InstagramAccountTypeSubheader from './InstagramAccountTypeSubheader'
import ManualBody from './ManualBody'
import PlatformErrorBanner from './PlatformErrorBanner'
import TaskBody from './TaskBody'

import './RecommendationCard.scss'

/**
 * Inform a user of the details of a recommendation, the current state of that
 * recommendation, and allow them to take action on it.
 */
export default function RecommendationCard({
  accessLevel,
  automationDef,
  displayPosition, // only used for metrics
  isLanguageSupported,
  isLessRelevant, // determined by personalization
  lastDeeplinkFeatureKey,
  onAnimatedTransition,
  onAutomationStarted,
  platform,
  progress,
  recommendationKey,
  showUpsellModal,
  spec,
  userId: platformUserId,
}: {
  accessLevel: AccessLevel
  automationDef: RecommendationAutomationDef
  displayPosition: number
  isLanguageSupported: boolean
  isLessRelevant: boolean
  isPlaybookCompleted: boolean
  lastDeeplinkFeatureKey: string | null
  onAnimatedTransition: (recommendationKey: RecommendationKey) => void
  onAutomationStarted: (recommendationKey: RecommendationKey) => void
  platform: PLATFORM
  progress: RecommendationCardDisplayData
  recommendationKey: string
  showUpsellModal: () => void
  spec: RecommendationDisplayWrapper
  userId: any
}) {
  const {
    bodyTitle,
    canUndo,
    concernTags,
    confirmTaskButtonText,
    continuingTaskButtonText,
    customActionDescription,
    explanationText,
    hasAutomatedGather,
    isTask,
    isPhoto,
    recommendationCardType,
    recommendationTitle,
    deeplinkShareUrl,
    settings,
    steps,
    taskButtonText,
    taskContext,
    taskTitle,
    taskUpdatedText,
    taskWarningText,
  } = spec
  const {
    automationErrors,
    initialState,
    recommendationState,
    lastGatheredTimestamp,
    lastUpdatedTimestamp,
    settingsSelected,
  } = progress

  const { activeTabId, recommendationCustomErrors } = useContext(SidebarContext)
  const errorStatus = recommendationCustomErrors?.[recommendationKey]

  // March 2025: Migration code to read initial state and automation errors
  // for tasks that are keyed by SettingKey instead of initial state as an
  // object with any keys. Other recommendations already use SettingKey and
  // initialState is not affected.
  let taskInitialState = initialState
  let taskSettingKey = null
  if (
    initialState &&
    Object.keys(initialState)?.length >= 1 &&
    Object.keys(initialState).some((key) => isValidSettingKey(key))
  ) {
    const settingKey = Object.keys(initialState).find((key) =>
      isValidSettingKey(key),
    )
    // Initial State migrated to always use setting keys
    taskInitialState = initialState[settingKey]
    taskSettingKey = settingKey
  }

  let taskAutomationErrors = automationErrors
  if (
    automationErrors &&
    Object.keys(automationErrors)?.length === 1 &&
    isValidSettingKey(Object.keys(automationErrors)[0])
  ) {
    taskAutomationErrors = Object.values(automationErrors)[0]
  }

  const [showExplanation, setShowExplanation] = useState(
    recommendationState === RecommendationState.INCOMPLETE || isTask,
  )

  const [isAnimatingCardStateTransition, setIsAnimatingCardStateTransition] =
    useState(false)
  const [
    animateFirstRenderForUpdatedCard,
    setAnimateFirstRenderForUpdatedCard,
  ] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)

  // @TODO: Move this into ScrollingContext
  const scrollToAnchor = useCallback((anchorId: string, smooth: boolean) => {
    scroller.scrollTo(anchorId, {
      smooth,
      duration: 250,
    })
  }, [])

  const toggleShowExplanation = useCallback(() => {
    setShowExplanation((prevValue) => !prevValue)
  }, [])

  const animateTransition = useCallback(
    (callback) => {
      setIsAnimatingCardStateTransition(true)
      setTimeout(() => {
        callback()
        setIsAnimatingCardStateTransition(false)
      }, 500)
      onAnimatedTransition(recommendationKey as RecommendationKey)
    },
    [onAnimatedTransition, recommendationKey],
  )

  const isPostDeeplinkGather = useMemo(
    () =>
      recommendationKey === lastDeeplinkFeatureKey &&
      lastGatheredTimestamp &&
      getTimeSinceInSeconds(lastGatheredTimestamp) <= 2,
    [lastDeeplinkFeatureKey, lastGatheredTimestamp, recommendationKey],
  )

  const [showGlow, setShowGlow] = useState(false)

  useEffect(() => {
    if (isPostDeeplinkGather) {
      // @TODO: Move this into ScrollingContext
      scrollToAnchor(recommendationKey, true)
      if (recommendationState === RecommendationState.INCOMPLETE) {
        setShowGlow(true)
        setTimeout(() => {
          setShowGlow(false)
        }, 5000)
      } else if (recommendationState === RecommendationState.FULFILLED) {
        setShowConfetti(true)
        setTimeout(() => {
          setShowConfetti(false)
        }, 1500)
      }
    }
  }, [
    isPostDeeplinkGather,
    recommendationKey,
    recommendationState,
    scrollToAnchor,
    setShowGlow,
  ])

  // When the completion state updates, trigger a toggle of show explanation
  useEffect(() => {
    setShowExplanation(
      recommendationState === RecommendationState.INCOMPLETE || isTask,
    )
    if (
      recommendationState === RecommendationState.UPDATED &&
      lastUpdatedTimestamp &&
      getTimeSinceInSeconds(lastUpdatedTimestamp) <= 2
    ) {
      if (recommendationCardType !== RecommendationCardType.MANUAL) {
        setAnimateFirstRenderForUpdatedCard(true)
        setTimeout(() => setAnimateFirstRenderForUpdatedCard(false), 500)
      }
      // Don't show confetti for Facebook content tasks or Twitter delete your posts
      if (
        recommendationKey !== FBRecommendationKey.FB_UNFRIEND &&
        recommendationKey !==
          TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE &&
        recommendationKey !==
          FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES &&
        recommendationKey !== FBRecommendationKey.FB_DELETE_POSTS &&
        recommendationKey !== FBRecommendationKey.FB_DELETE_PAGE_LIKES
      ) {
        setShowConfetti(true)
        setTimeout(() => {
          setShowConfetti(false)
        }, 1500)
      }
    }
  }, [
    recommendationState,
    scrollToAnchor,
    recommendationKey,
    setAnimateFirstRenderForUpdatedCard,
    recommendationCardType,
    lastUpdatedTimestamp,
    isTask,
  ])

  const setUpdateAutomation = useCallback(() => {
    startAutomationMachineForRecommendation(
      platform,
      platformUserId,
      recommendationKey,
      AutomationType.UPDATE,
      null,
      recommendationKey === lastDeeplinkFeatureKey,
    )
  }, [lastDeeplinkFeatureKey, platform, platformUserId, recommendationKey])

  const setUndoAutomation = useCallback(() => {
    startAutomationMachineForRecommendation(
      platform,
      platformUserId,
      recommendationKey,
      AutomationType.UNDO,
      null,
    )
  }, [platform, platformUserId, recommendationKey])

  // On click handlers
  const onClickUpdate = useCallback(async () => {
    await updatePlatformUserAccountData(
      platform as PLATFORM,
      platformUserId,
      {},
    )
    onAutomationStarted(recommendationKey as RecommendationKey)
    await communication.sendSetActiveTabForPlatform(platform, activeTabId)
    setUpdateAutomation()
  }, [
    onAutomationStarted,
    activeTabId,
    platform,
    platformUserId,
    recommendationKey,
    setUpdateAutomation,
  ])

  const onClickMarkAsDone = useCallback(async () => {
    animateTransition(() => {
      updatePlatformUserAccountData(platform as PLATFORM, platformUserId, {})
      state.setRecommendationStatus(
        platform,
        platformUserId,
        recommendationKey,
        {
          updated: true,
        },
      )
      setShowExplanation(false)

      // Log manual update action
      addUserActionToLog({
        platform,
        platformUserId,
        actionType: ActionType.ManualUpdate,
        recommendationKey,
        settingsSelected: undefined,
        nUnitsUpdated: undefined,
      })
    })
  }, [animateTransition, platform, platformUserId, recommendationKey])

  const onClickSkip = useCallback(() => {
    animateTransition(() => {
      updatePlatformUserAccountData(platform as PLATFORM, platformUserId, {})
      state.setRecommendationStatus(
        platform,
        platformUserId,
        recommendationKey,
        {
          skipped: true,
        },
      )
      setShowExplanation(false)

      // Log skip action
      addUserActionToLog({
        platform,
        platformUserId,
        actionType: ActionType.Skip,
        recommendationKey,
        settingsSelected: undefined,
        nUnitsUpdated: undefined,
      })
    })
  }, [animateTransition, platform, platformUserId, recommendationKey])

  const onClickUndoAutomated = useCallback(async () => {
    await updatePlatformUserAccountData(
      platform as PLATFORM,
      platformUserId,
      {},
    )
    onAutomationStarted(recommendationKey as RecommendationKey)
    await communication.sendSetActiveTabForPlatform(platform, activeTabId)
    setUndoAutomation()
  }, [
    onAutomationStarted,
    activeTabId,
    platform,
    platformUserId,
    recommendationKey,
    setUndoAutomation,
  ])

  const onClickUndoManual = useCallback(() => {
    animateTransition(() => {
      updatePlatformUserAccountData(platform as PLATFORM, platformUserId, {})
      state.setRecommendationStatus(
        platform,
        platformUserId,
        recommendationKey,
        {
          undoUpdated: true,
        },
      )
      setShowExplanation(true)

      // Log manual update action
      addUserActionToLog({
        platform,
        platformUserId,
        actionType: ActionType.ManualUndo,
        recommendationKey,
        settingsSelected: undefined,
        nUnitsUpdated: undefined,
      })
    })
  }, [animateTransition, platform, platformUserId, recommendationKey])

  const onClickUndoMarkAsSkipped = useCallback(() => {
    animateTransition(() => {
      updatePlatformUserAccountData(platform as PLATFORM, platformUserId, {})
      state.setRecommendationStatus(
        platform,
        platformUserId,
        recommendationKey,
        {
          undoSkipped: true,
        },
      )
      setShowExplanation(true)

      // Log undo skip action
      addUserActionToLog({
        platform,
        platformUserId,
        actionType: ActionType.UndoSkip,
        recommendationKey,
        settingsSelected: undefined,
        nUnitsUpdated: undefined,
      })
    })
  }, [animateTransition, platform, platformUserId, recommendationKey])

  const onClickCheckSettings = useCallback(async () => {
    const setGatherAutomation = () => {
      startAutomationMachineForRecommendation(
        platform,
        platformUserId,
        recommendationKey,
        AutomationType.GATHER,
        null,
      )
    }

    await updatePlatformUserAccountData(
      platform as PLATFORM,
      platformUserId,
      {},
    )
    onAutomationStarted(recommendationKey as RecommendationKey)
    await communication.sendSetActiveTabForPlatform(platform, activeTabId)
    setGatherAutomation()
  }, [
    onAutomationStarted,
    activeTabId,
    platform,
    platformUserId,
    recommendationKey,
  ])

  const onClickUpsell = useCallback(() => {
    showUpsellModal()
  }, [showUpsellModal])

  const handleTaskConfirmClick = useCallback(async () => {
    await communication.sendSetActiveTabForPlatform(platform, activeTabId)
    // Experiment state management and flows may break our normal assumptions.
    // Ensure progress for this recommendation is initialized before update.
    await state.updateRecommendation(
      platform,
      platformUserId,
      recommendationKey as RecommendationKeyEnum,
      {},
      { doNotUpdateLastUpdatedTimestamp: true },
    )
    onClickUpdate()
  }, [activeTabId, platform, platformUserId, recommendationKey, onClickUpdate])

  let subheader
  if (platform === PLATFORM.INSTAGRAM) {
    // If this recommendation is only for public accounts
    if (spec.accountTypes?.length === 1 && spec.accountTypes[0] === 'Public') {
      subheader = (
        <InstagramAccountTypeSubheader
          type={InstagramAccountVisibility.PUBLIC}
        />
      )
    }
  }

  let addlExplanationTextClassName
  if (showExplanation === null) {
    addlExplanationTextClassName = 'initial'
  } else if (showExplanation === false) {
    addlExplanationTextClassName = 'hide'
  } else {
    addlExplanationTextClassName = 'show'
  }

  const addClickable = explanationText ? '' : 'not-clickable'

  let content: ReactNode | undefined
  switch (recommendationCardType) {
    case RecommendationCardType.AUTOMATED:
      content = (
        <AutomatedBody
          accessLevel={accessLevel}
          animateFirstRenderForUpdatedCard={animateFirstRenderForUpdatedCard}
          isAnimatingCardStateTransition={isAnimatingCardStateTransition}
          automationDef={automationDef}
          automationErrors={automationErrors}
          canUndo={canUndo}
          customActionDescription={customActionDescription}
          defaultBodyTitle={bodyTitle}
          initialState={initialState}
          isLanguageSupported={isLanguageSupported}
          isLessRelevant={isLessRelevant}
          isPhoto={isPhoto}
          lastGatheredTimestamp={lastGatheredTimestamp}
          lastUpdatedTimestamp={lastUpdatedTimestamp}
          onClickAction={onClickUpdate}
          onClickMarkAsDone={onClickMarkAsDone}
          onClickScanSettings={onClickCheckSettings}
          onClickSkip={onClickSkip}
          onClickUndo={onClickUndoAutomated}
          onClickUndoMarkAsDone={onClickUndoManual}
          onClickUndoSkip={onClickUndoMarkAsSkipped}
          onClickUpsell={onClickUpsell}
          platform={platform}
          recommendationKey={recommendationKey}
          recommendationState={recommendationState}
          settings={settings}
          settingsSelected={settingsSelected}
          truncate={isPostDeeplinkGather || false}
          userId={platformUserId}
          locked={!isLanguageSupported}
          showGlow={showGlow}
        />
      )
      break
    case RecommendationCardType.FACEBOOK_PHOTO_UNTAGGING:
      content = (
        <FbPhotoUntaggingBody
          accessLevel={accessLevel}
          animateFirstRenderForUpdatedCard={animateFirstRenderForUpdatedCard}
          isAnimatingCardStateTransition={isAnimatingCardStateTransition}
          customActionDescription={customActionDescription}
          defaultBodyTitle={bodyTitle}
          initialState={taskInitialState}
          languageLocked={!isLanguageSupported}
          recommendationState={recommendationState}
          lastGatheredTimestamp={lastGatheredTimestamp}
          platform={platform}
          platformUserId={platformUserId}
          recommendationKey={recommendationKey}
          onClickCheckSettings={onClickCheckSettings}
          onClickUpsell={onClickUpsell}
          locked={!isLanguageSupported}
        />
      )
      break
    case RecommendationCardType.TASK:
      if (recommendationKey !== FBRecommendationKey.FB_UNFRIEND) {
        content = (
          <TaskBody
            accessLevel={accessLevel}
            automationErrors={taskAutomationErrors}
            confirmTaskButtonText={confirmTaskButtonText}
            continuingTaskButtonText={continuingTaskButtonText}
            initialState={taskInitialState}
            lastUpdatedTimestamp={lastUpdatedTimestamp}
            locked={false}
            onConfirmClick={handleTaskConfirmClick}
            onClickUpsell={onClickUpsell}
            onClickCheckSettings={onClickCheckSettings}
            platform={platform}
            platformUserId={platformUserId}
            recommendationKey={recommendationKey}
            recommendationState={recommendationState}
            settings={settings}
            settingsSelected={settingsSelected}
            taskButtonText={taskButtonText}
            taskContext={taskContext}
            taskTitle={taskTitle}
            taskUpdatedText={taskUpdatedText}
            taskWarningText={taskWarningText}
          />
        )
      } else {
        content = (
          <FbUnfriendBody
            accessLevel={accessLevel}
            handleShowUpsell={onClickUpsell}
            languageLocked={!isLanguageSupported}
            platformUserId={platformUserId}
            unfriendState={taskInitialState as State}
          />
        )
      }
      break
    default:
      content = (
        <ManualBody
          isAnimatingCardStateTransition={isAnimatingCardStateTransition}
          customActionDescription={customActionDescription}
          defaultBodyTitle={bodyTitle}
          recommendationState={recommendationState}
          onClickAction={onClickMarkAsDone}
          onClickSkip={onClickSkip}
          onClickUndo={onClickUndoManual}
          onClickUndoSkip={onClickUndoMarkAsSkipped}
          steps={steps}
          locked={false}
        />
      )
      break
  }

  const [showCopyToast, setShowCopyToast] = useState(false)
  const handleCopyDeeplink = useCallback(() => {
    if (deeplinkShareUrl) {
      navigator.clipboard.writeText(deeplinkShareUrl)
    }
    setShowCopyToast(true)
    setTimeout(() => {
      setShowCopyToast(false)
    }, 2000)
  }, [deeplinkShareUrl])

  const logEventProps = {
    component: 'RecommendationCard',
    cardType: recommendationCardType,
    concernTags,
    displayPosition,
    hasAutomatedGather,
    isLessRelevant,
    isNotGathered: !initialState,
    platform,
    recommendationKey,
    recommendationState,
    settingsSelected: settingsSelected
      ? Object.keys(settingsSelected).filter(
          (setting) => settingsSelected[setting],
        )
      : [],
  }

  return (
    <LogContextProvider {...logEventProps}>
      <ImpressionLogger
        eventName="recommendation:view"
        eventProps={logEventProps}
      >
        <section
          aria-labelledby={`${recommendationKey}-title`}
          className={clsx(
            'RecommendationCard',
            recommendationCardType ===
              RecommendationCardType.FACEBOOK_PHOTO_UNTAGGING &&
              'facebook-photo-untagging',
          )}
        >
          {showCopyToast && <Toast color="green" text="Link copied" />}
          {showConfetti && <Confetti className="confetti" />}
          <Header
            accessLevel={accessLevel}
            concernTags={concernTags}
            hasAutomatedGather={hasAutomatedGather}
            isTask={isTask}
            isLanguageSupported={isLanguageSupported}
            isLessRelevant={isLessRelevant}
            isPhoto={isPhoto}
            locked={!isLanguageSupported}
            onReScanClick={onClickCheckSettings}
            recommendationCardType={recommendationCardType}
            recommendationState={recommendationState}
          />
          {errorStatus && (
            <PlatformErrorBanner
              message={errorStatus.body}
              platform={platform}
            />
          )}
          {recommendationCardType === RecommendationCardType.TASK &&
            !errorStatus &&
            (platform === PLATFORM.TWITTER ||
              platform === PLATFORM.REDDIT ||
              platform === PLATFORM.BLUESKY ||
              platform === PLATFORM.FACEBOOK ||
              platform === PLATFORM.LINKEDIN) &&
            taskInitialState?.result && (
              <Banner
                title={taskInitialState.result.title}
                detail={taskInitialState.result.detail}
                footerText={taskInitialState.result.helper?.text}
                footerLink={taskInitialState.result.helper?.link}
                footerLinkText={taskInitialState.result.helper?.linkText}
                onClickClose={() => {
                  let updatedInitialState = { ...taskInitialState }
                  delete updatedInitialState.result

                  if (taskSettingKey) {
                    updatedInitialState = {
                      [taskSettingKey]: updatedInitialState,
                    }
                  }

                  state.updateRecommendation(
                    platform,
                    platformUserId,
                    recommendationKey as RecommendationKeyEnum,
                    { initialState: updatedInitialState },
                    { doNotUpdateLastUpdatedTimestamp: true },
                  )
                }}
              />
            )}
          <div className={clsx('recommendation-title-wrapper', addClickable)}>
            {subheader}
            <div className="recommendation-title">
              <h3
                className={clsx(
                  'title',
                  'heading3',
                  'bold',
                  'black',
                  showGlow && 'glow',
                )}
              >
                <div className="title-text" id={`${recommendationKey}-title`}>
                  {recommendationTitle}
                </div>
                {deeplinkShareUrl && (
                  <InteractiveElement
                    className="share-url"
                    aria-label={`Copy ${recommendationKey} deeplink`}
                    role="button"
                  >
                    <CopyToClipboard
                      onCopy={handleCopyDeeplink}
                      text={deeplinkShareUrl}
                    >
                      <img
                        alt="copy link"
                        srcSet={LinkIcon}
                        className="link-icon"
                      />
                    </CopyToClipboard>
                  </InteractiveElement>
                )}
              </h3>
              {explanationText && !showExplanation ? (
                <InteractiveElement
                  aria-label="Show Explanation"
                  aria-expanded={showExplanation}
                  className="toggle-explanation-button"
                  onClick={toggleShowExplanation}
                >
                  <SectionExpandIcon
                    aria-hidden
                    className="toggle-explanation-icon"
                  />
                </InteractiveElement>
              ) : (
                <InteractiveElement
                  aria-label="Hide Explanation"
                  aria-expanded={showExplanation}
                  className="toggle-explanation-button"
                  onClick={toggleShowExplanation}
                >
                  <SectionCollapseIcon
                    aria-hidden
                    className="toggle-explanation-icon"
                  />
                </InteractiveElement>
              )}
            </div>
            <div
              className={`recommendation-explanation-text ${addlExplanationTextClassName}`}
            >
              <span className="body2">{explanationText}</span>
            </div>
          </div>
          {content}
        </section>
      </ImpressionLogger>
    </LogContextProvider>
  )
}
